/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {Popover, Transition} from '@headlessui/react'
import clsx from 'clsx'
import {useDebouncedCallback} from 'utils/hooks/useDebounce'
import UiProbabilityPickerItemsList from './UiProbabilityPickerComponents/UiProbabilityPickerItemsList'

interface UiProbabilityPickerProps {
    value?: number | null
    onChange: (value: number) => void
    className?: string
    disabled?: boolean
}

const UiProbabilityPicker: React.FC<UiProbabilityPickerProps> = ({
    value,
    onChange,
    className,
    disabled,
}) => {
    const percentages = [0, 20, 50, 80, 100]

    // Debounce the onChange callback to improve performance
    const debouncedOnChange = useDebouncedCallback(
        (percentage: number) => onChange(percentage),
        300
    )

    const getProbColor = (p: number) => {
        if (p >= 100) return 'bg-emerald-600'
        if (p >= 80) return 'bg-green-500'
        if (p >= 60) return 'bg-amber-500'
        if (p >= 40) return 'bg-orange-500'
        return 'bg-red-500'
    }

    const getProbGradient = (p: number) => {
        if (p >= 100)
            return 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
        if (p >= 75)
            return 'bg-gradient-to-r from-green-500 to-green-600 text-white'
        if (p >= 50)
            return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
        if (p >= 25)
            return 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
        return 'bg-gradient-to-r from-red-500 to-red-600 text-white'
    }

    return (
        <Popover
            className={clsx(
                'UiProbabilityPicker relative inline-flex',
                className
            )}
        >
            <Popover.Button
                disabled={disabled}
                className={clsx(
                    'px-2.5 py-1 rounded-full text-xs font-bold shadow-sm transition-opacity focus:outline-none',
                    disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:opacity-80',
                    getProbGradient(value ?? 0)
                )}
            >
                {value ?? 0}%
            </Popover.Button>

            <Transition
                as={React.Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-1 scale-95"
            >
                <Popover.Panel className="UiProbabilityPickerMenu absolute left-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-2.5 flex items-center gap-2 origin-top-left">
                    {({ close }) => (
                        <div className="flex items-center gap-2">
                            <UiProbabilityPickerItemsList
                                percentages={percentages}
                                onSelect={(p) => {
                                    debouncedOnChange(p)
                                    close()
                                }}
                                getProbColor={getProbColor}
                            />
                        </div>
                    )}
                </Popover.Panel>
            </Transition>
        </Popover>
    )
}

export default UiProbabilityPicker
